import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { pushRecentDrawer } from '@/utils/recentDrawers';

/**
 * useListDrawer — drives a record drawer driven by a list of items, with:
 *   • URL deep-linking via ?{paramKey}={id}   (default param: 'open')
 *   • prev/next navigation that wraps the list and updates the URL
 *   • automatic recording of opened items into the global "Recent Drawers" store
 */
const DEFAULT_LABEL_FIELDS = ['title', 'name', 'subject', 'label', 'description'];

function defaultLabel(item, idKey) {
  if (!item) return '';
  for (const f of DEFAULT_LABEL_FIELDS) {
    if (typeof item[f] === 'string' && item[f].trim()) return item[f];
  }
  return String(item[idKey] ?? '');
}

export default function useListDrawer({
  items = [],
  idKey = 'id',
  paramKey = 'open',
  getLabel,
}) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const openId = params.get(paramKey);

  const index = useMemo(() => {
    if (!openId) return -1;
    return items.findIndex((it) => it?.[idKey] === openId);
  }, [items, openId, idKey]);

  const active = index >= 0 ? items[index] : null;

  const setActive = useCallback(
    (item) => {
      const next = new URLSearchParams(params);
      if (item) next.set(paramKey, item[idKey]);
      else next.delete(paramKey);
      setParams(next, { replace: false });
    },
    [params, setParams, paramKey, idKey],
  );

  const openByOffset = useCallback(
    (offset) => {
      if (!items.length) return;
      const base = index < 0 ? 0 : index;
      const next = (base + offset + items.length) % items.length;
      setActive(items[next]);
    },
    [items, index, setActive],
  );

  const openPrev = useCallback(() => openByOffset(-1), [openByOffset]);
  const openNext = useCallback(() => openByOffset(1), [openByOffset]);

  // Keyboard shortcuts removed per product decision — navigation now relies on
  // explicit prev/next buttons in DrawerPanel only.

  // Track every distinct drawer open into the recent-drawers store.
  const lastTrackedRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    const id = active[idKey];
    if (!id || lastTrackedRef.current === id) return;
    lastTrackedRef.current = id;
    const label = (getLabel ? getLabel(active) : defaultLabel(active, idKey)) || String(id);
    const path = `${location.pathname}?${paramKey}=${encodeURIComponent(id)}`;
    pushRecentDrawer({
      id,
      label,
      path,
      severity: active.severity ?? active.priority ?? null,
    });
  }, [active, idKey, paramKey, location.pathname, getLabel]);

  return {
    active,
    setActive,
    openPrev,
    openNext,
    index,
    hasPrev: items.length > 1,
    hasNext: items.length > 1,
    total: items.length,
  };
}
